import React from 'react';

// Icons copied verbatim from the design source (mobile-app.jsx in the
// design handoff). Lightweight stroke-based SVGs sized via the CSS class
// on the consuming element.

type IconProps = React.SVGProps<SVGSVGElement>;

export const ChipStackIcon: React.FC<IconProps> = (p) => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.6' {...p}>
    <ellipse cx='12' cy='7' rx='7' ry='2.2' />
    <path d='M5 7v4c0 1.2 3.1 2.2 7 2.2s7-1 7-2.2V7' />
    <path d='M5 11v4c0 1.2 3.1 2.2 7 2.2s7-1 7-2.2v-4' />
  </svg>
);

export const ClockIcon: React.FC<IconProps> = (p) => (
  <svg
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='1.8'
    strokeLinecap='round'
    {...p}>
    <circle cx='12' cy='12' r='9' />
    <path d='M12 7v5l3 2' />
  </svg>
);

export const StarOutlineIcon: React.FC<IconProps> = (p) => (
  <svg
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='1.8'
    strokeLinejoin='round'
    {...p}>
    <path d='M12 3l2.5 6.5H21l-5.2 4 2 6.5L12 16l-5.8 4 2-6.5L3 9.5h6.5z' />
  </svg>
);

// ----------------------------------------------------------------------
// Filter-chip icons. Sourced verbatim from the Skullcrusher design's
// MIcon set in mobile-app.jsx. fill/stroke use currentColor so each chip
// state (default / hover / active) can recolor them via CSS.
// ----------------------------------------------------------------------

export const CardsIcon: React.FC<IconProps> = (p) => (
  <svg
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='1.7'
    strokeLinejoin='round'
    {...p}>
    <rect x='7' y='5' width='11' height='15' rx='1.5' transform='rotate(-8 12 12)' />
  </svg>
);

export const BulletIcon: React.FC<IconProps> = (p) => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5' {...p}>
    <circle cx='12' cy='12' r='3.2' fill='currentColor' stroke='none' />
    <circle cx='12' cy='12' r='6' />
  </svg>
);

export const SnowflakeIcon: React.FC<IconProps> = (p) => (
  <svg
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='1.6'
    strokeLinecap='round'
    {...p}>
    <path d='M12 2 V22 M2 12 H22 M4.5 4.5 L19.5 19.5 M19.5 4.5 L4.5 19.5' />
  </svg>
);

export const CoinIcon: React.FC<IconProps> = (p) => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' {...p}>
    <circle cx='12' cy='12' r='9' />
    <path d='M12 7v10M9 9h4.5a2 2 0 0 1 0 4H10a2 2 0 0 0 0 4h5' strokeLinecap='round' />
  </svg>
);

export const RocketIcon: React.FC<IconProps> = (p) => (
  <svg
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='1.7'
    strokeLinecap='round'
    strokeLinejoin='round'
    {...p}>
    <path d='M14 4c4 1 6 4 6 6-2 0-3 1-4 2l-4 4-4-4 4-4c1-1 2-3 2-4z' />
    <path d='M9 13l-3 3 2 2 3-3M5 19c-1 1-2 1-2 1s0-1 1-2' />
  </svg>
);

export const SpadeIcon: React.FC<IconProps> = (p) => (
  <svg viewBox='0 0 24 24' fill='currentColor' {...p}>
    <path d='M12 2c-1 3-4 5-6 7-2 2-3 4-3 6 0 2.2 1.8 4 4 4 1.5 0 2.7-.8 3.4-2-.4 1.4-1.4 2.8-2.4 3.5h8c-1-.7-2-2.1-2.4-3.5.7 1.2 1.9 2 3.4 2 2.2 0 4-1.8 4-4 0-2-1-4-3-6-2-2-5-4-6-7z' />
  </svg>
);

export const DiamondIcon: React.FC<IconProps> = (p) => (
  <svg viewBox='0 0 24 24' fill='currentColor' {...p}>
    <path d='M12 2 L21 12 L12 22 L3 12 Z' />
  </svg>
);

// The Sit & Go ladder's seat toggle (owner, 2026-09-10): a table seen from
// above, with the seats that format fills — two facing players, or six around
// the oval. Same stroke weight as the chip stack so they sit in the same row.
export const HeadsUpIcon: React.FC<IconProps> = (p) => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.6' {...p}>
    <circle cx='7' cy='8' r='2.6' />
    <path d='M2.5 18c0-2.6 2-4.5 4.5-4.5s4.5 1.9 4.5 4.5' />
    <circle cx='17' cy='8' r='2.6' />
    <path d='M12.5 18c0-2.6 2-4.5 4.5-4.5s4.5 1.9 4.5 4.5' />
  </svg>
);

export const SixSeatIcon: React.FC<IconProps> = (p) => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.6' {...p}>
    <ellipse cx='12' cy='12' rx='6' ry='3.4' />
    <g fill='currentColor' stroke='none'>
      <circle cx='21.3' cy='12' r='1.4' />
      <circle cx='2.7' cy='12' r='1.4' />
      <circle cx='16.7' cy='17.6' r='1.4' />
      <circle cx='7.3' cy='17.6' r='1.4' />
      <circle cx='16.7' cy='6.4' r='1.4' />
      <circle cx='7.3' cy='6.4' r='1.4' />
    </g>
  </svg>
);
